import React, { useState, useEffect, useMemo, useRef } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import {
  CostElement,
  CostInput,
  Desc,
  EditIcon,
  FormGroupLabel,
  FormItem,
  LinkSpan,
  UnitSpan,
} from "./FormElements";
import {
  anchorColor,
  colorAdditional,
  colorBg,
  colorBorder,
  colorGray,
  colorGrayNew,
  colorGreen,
  colorGreenHover,
  colorLightGray,
  colorStatusActive,
  colorTextNew,
  sidenavColor,
} from "../../../utils/palette";
import {
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../helpers";
import Error from "@/shared/components/form/Error";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { AddFormButton, Icon, ActionIconContainer } from "../TableElements";
import CreatableSelect from "react-select/creatable";
import {
  formInputStyles,
  formStyles,
  openDropdownStyles,
} from "../../constants/styles";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchServicesTasks,
  fetchServiceTaskTemplates,
} from "../../../features/servicesTasks/servicesTasksSlice";
import { updateFormData } from "../../../features/formData/formDataSlice";
import AddButton from "./AddButton";
import { components } from "react-select";
import ServiceTasksFormPopup from "../../../components/Services/ServiceTasksFormPopup";
import { postApi } from "../../../api/getUser";
import { updateServiceTaskOptions } from "../../../components/Services/ServicesFormNew";
import { NATEXT, taskLogStatusMap } from "../../constants";
import AddPartsPopupNew from "../../../components/Inventory/components/AddPartsPopupNew";
import UnitsConsumedPopupNew from "../../../components/Inventory/components/UnitsConsumedPopupNew";
import SubscribePopup from "../SubscribePopup";
import ActionPopup from "../ActionPopup";
import { useAuth } from "../../../contexts/AuthContext";
import CustomMenuList from "./CustomMenuList";
import ServiceTaskTemplatePopupNew from "./ServiceTaskTemplatePopupNew";
import TemplateConfirmationPopup from "./TemplateConfirmationPopup";
import MultiValueOption from "./MultiValueOption";
import { StickyGroupHeading } from "../../../components/Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import useClickAway from "../../custom-hooks/useClickAway";
import styled from "styled-components";

// Refactored version — serviceTasks is the single source of truth.
// Each entry has: action (1|2|3), service_task_detail_id, batches[] inline.
// No serviceTasksRef, no partBatches.
const ServiceTasksNew = ({
  showAddServiceModal,
  setShowAddServiceModal,
  serviceTasks,
  setServiceTasks,
  partsCostTotal,
  setPartsCostTotal,
  laborCostTotal,
  setLaborCostTotal,
  tax,
  setTax,
  discount,
  setDiscount,
  total,
  setTotal,
  grandTotal,
  setGrandTotal,
  showServiceTasksError,
  setShowServiceTasksError,
  showDiscountError,
  workOrderStatus,
  module = "workorder",
  showAddServiceTaskFormPopup,
  setShowAddServiceTaskFormPopup,
  selectedVehicleId,
  serviceTaskOptions,
  setServiceTaskOptions,
  serviceTasksChanged,
  setServiceTasksChanged,
  vehicle,
  template,
  setTemplate,
  isEquipment = false,
  isCollapsible = false,
  helperText = "",
  woId = null,
  taskTimersRef = null,
  resumeWO = null,
  technician = null,
  manualTimeRef = null,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { currency } = useSelector((state) => state.units);
  const { roleId, woTimerMode, userId } = useSelector((state) => state.user);
  const users = useSelector((state) => state.users.list);
  const reduxServiceTasks = useSelector((state) => state.servicesTasks.list);
  const { tag } = useAuth();

  const serviceTasksErrorMsg = t("invalid_st_msg");

  const [showAddPartsPopup, setShowAddPartsPopup] = useState(false);
  const [showUnitsConsumedPopup, setShowUnitsConsumedPopup] = useState(false);

  // The detail_id of the task for which parts are being added/edited
  const [activeDetailId, setActiveDetailId] = useState(null);
  // The suggested part that triggered AddPartsPopup (for preselection flow)
  const [activeSuggestedPart, setActiveSuggestedPart] = useState(null);

  const [fromUnitsConsumed, setFromUnitsConsumed] = useState(false);
  // Accumulates batches across the entire add-parts session (never written to serviceTasks until Submit)
  const [sessionBatches, setSessionBatches] = useState([]);

  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [subscribePopupErrMsg, setsubscribePopupErrMsg] = useState("");

  // Per-task expand / notes / timer (keyed by service_task_detail_id)
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());
  const [noteInputVisible, setNoteInputVisible] = useState({});
  const [showSuggestedPartsFor, setShowSuggestedPartsFor] = useState(null);
  const [showLogsFor, setShowLogsFor] = useState(null);
  const [taskTimers, setTaskTimers] = useState({});
  const timerTickRef = useRef(null);
  // Tracks which detailId has a timer action in-flight; blocks duplicate clicks
  const timerPendingRef = useRef(null);
  const [timerPendingId, setTimerPendingId] = useState(null);

  // Override labor time popup: { detailId, minutes: '' } | null
  const [overrideTask, setOverrideTask] = useState(null);
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Confirmation popup for starting/resuming a task while WO is paused
  // holds detailId of the pending action; null = no popup
  const [pendingTaskAction, setPendingTaskAction] = useState(null);
  // Set to true before re-invoking start/resume after WO resume confirmation
  const skipWoPausedGuardRef = useRef(false);
  const [resumingWO, setResumingWO] = useState(false);

  const [editPartsLaborTask, setEditPartsLaborTask] = useState(null);
  const [showEditPartsLaborPopup, setShowEditPartsLaborPopup] = useState(null);

  // Manual time entry state keyed by detailId: { hours, minutes }
  const [manualTime, setManualTime] = useState({});

  // Global timer tick
  useEffect(() => {
    timerTickRef.current = setInterval(() => {
      setTaskTimers((prev) => {
        const hasRunning = Object.values(prev).some(
          (t) => t.status === "running",
        );
        if (!hasRunning) return prev;
        const now = Date.now();
        const next = { ...prev };
        for (const id of Object.keys(next)) {
          if (next[id].status === "running") {
            next[id] = {
              ...next[id],
              elapsed:
                next[id].baseElapsed +
                Math.floor((now - next[id].lastStartTime) / 60000),
            };
          }
        }
        return next;
      });
    }, 60000);
    return () => clearInterval(timerTickRef.current);
  }, []);

  // Hydrate timers from API data on edit load (runs once when tasks arrive)
  const timersHydratedRef = useRef(false);
  useEffect(() => {
    if (timersHydratedRef.current || serviceTasks.length === 0) return;
    const hasInitialData = serviceTasks.some(
      (t) => t.initialTimerStatus !== undefined,
    );
    if (!hasInitialData) return;
    timersHydratedRef.current = true;
    const initial = {};
    for (const t of serviceTasks) {
      if (!t.initialTimerStatus || t.initialTimerStatus === "idle") continue;
      let baseElapsed = t.initialElapsed || 0;
      let lastStartTime = null;
      if (t.initialTimerStatus === "running") {
        if (t.initialStartTime) {
          const extraSec = Math.floor(Date.now() / 1000) - t.initialStartTime;
          baseElapsed = Math.floor(
            (baseElapsed * 60 + Math.max(0, extraSec)) / 60,
          );
        }
        lastStartTime = Date.now();
      }
      initial[t.service_task_detail_id] = {
        status: t.initialTimerStatus,
        elapsed: baseElapsed,
        baseElapsed,
        lastStartTime,
      };
    }
    if (Object.keys(initial).length > 0) {
      setTaskTimers(initial);
    }
  }, [serviceTasks]);

  // Keep taskTimersRef in sync so WorkOrdersFormNew can read it at any time
  useEffect(() => {
    if (taskTimersRef) {
      taskTimersRef.current = taskTimers;
    }
  }, [taskTimers]);

  // Keep manualTimeRef in sync so WorkOrdersFormNew can read it at submit time
  useEffect(() => {
    if (manualTimeRef) {
      manualTimeRef.current = manualTime;
    }
  }, [manualTime]);

  // Hydrate manual time state from initialElapsed when tasks load
  const manualTimeHydratedRef = useRef(false);
  useEffect(() => {
    if (manualTimeHydratedRef.current || serviceTasks.length === 0) return;
    const hasInitialData = serviceTasks.some(
      (t) => t.initialElapsed !== undefined,
    );
    if (!hasInitialData) return;
    manualTimeHydratedRef.current = true;
    const initial = {};
    for (const t of serviceTasks) {
      const totalMinutes = t.initialElapsed || 0;
      initial[t.service_task_detail_id] = {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      };
    }
    setManualTime(initial);
  }, [serviceTasks]);

  const getTimerState = (detailId) =>
    taskTimers[detailId] || {
      status: "idle",
      elapsed: 0,
      baseElapsed: 0,
      lastStartTime: null,
    };

  const handleManualTimeChange = (detailId, field, value) => {
    const prev = manualTime[detailId] || { hours: 0, minutes: 0 };
    const newTime = { ...prev, [field]: Number(value) };
    setManualTime((s) => ({ ...s, [detailId]: newTime }));

    const totalHours = newTime.hours + newTime.minutes / 60;
    let hourlyRate = 0;
    if (roleId === 4) {
      hourlyRate = +users?.find((u) => u.user_id === userId)?.hourly_rate || 0;
    } else if ((roleId === 1 || roleId === 2) && technician) {
      hourlyRate = +technician.hourly_rate || 0;
    }

    if (hourlyRate > 0) {
      const newLaborCost = +(totalHours * hourlyRate).toFixed(2);
      setServiceTasks((tasks) =>
        tasks.map((t) => {
          if (t.service_task_detail_id !== detailId) return t;
          const newTotalCost = +(newLaborCost + (+t.partsCost || 0)).toFixed(2);
          return { ...t, laborCost: newLaborCost, totalCost: newTotalCost };
        }),
      );
    }
  };

  const callTaskProgressApi = async (detailId, task_action) => {
    const res = await postApi(
      {
        querystring: "addUpdateTaskProgress",
        service_task_detail_id: detailId,
        task_action,
        action_time: Math.floor(Date.now() / 1000),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      "workOrder",
    );
    if (res?.success && res.service_task_detail?.logs) {
      const updatedLogs = res.service_task_detail.logs;
      setServiceTasks((prev) =>
        prev.map((t) =>
          t.service_task_detail_id === detailId
            ? { ...t, logs: updatedLogs }
            : t,
        ),
      );
    }
    return res;
  };

  const getLatestServiceTaskData = (taskId, key, defaultValue = []) => {
    const latest = reduxServiceTasks.find((t) => t.service_task_id === taskId);
    return latest?.[key] || defaultValue;
  };

  const handleStartTask = async (detailId) => {
    if (workOrderStatus === 2 && resumeWO && !skipWoPausedGuardRef.current) {
      setPendingTaskAction(detailId);
      return;
    }
    skipWoPausedGuardRef.current = false;
    if (timerPendingRef.current) return;
    timerPendingRef.current = detailId;
    setTimerPendingId(detailId);
    try {
      const task = serviceTasks.find(
        (t) => t.service_task_detail_id === detailId && t.action !== 3,
      );
      if (!task) return;

      if (task.action === 1) {
        const res = await postApi(
          {
            source: "web",
            sync_version: "v2",
            operation: "create",
            querystring: "addEditServiceTaskDetails",
            service_task_details: [
              {
                service_task_detail_id: task.service_task_detail_id,
                service_task_id: task.service_task_id,
                service_id: task.service_id || null,
                wo_id: woId,
                parts_cost: task.partsCost ? +task.partsCost : 0,
                labor_cost: task.laborCost ? +task.laborCost : 0,
                total_cost: task.totalCost ? +task.totalCost : 0,
                action: 1,
                comments: task.comments || "",
              },
            ],
          },
          "workOrder",
        );
        if (!res?.success) return;

        setServiceTasks((prev) =>
          prev.map((t) =>
            t.service_task_detail_id === detailId ? { ...t, action: 2 } : t,
          ),
        );
      }

      setTaskTimers((prev) => ({
        ...prev,
        [detailId]: {
          status: "running",
          elapsed: 0,
          baseElapsed: 0,
          lastStartTime: Date.now(),
        },
      }));
      await callTaskProgressApi(detailId, "start");
    } finally {
      timerPendingRef.current = null;
      setTimerPendingId(null);
    }
  };

  const handlePauseTask = async (detailId) => {
    if (timerPendingRef.current) return;
    timerPendingRef.current = detailId;
    setTimerPendingId(detailId);
    try {
      setTaskTimers((prev) => {
        const t = prev[detailId] || {};
        const now = Date.now();
        const newBase =
          (t.baseElapsed || 0) +
          Math.floor((now - (t.lastStartTime || now)) / 60000);
        return {
          ...prev,
          [detailId]: {
            ...t,
            status: "paused",
            elapsed: newBase,
            baseElapsed: newBase,
            lastStartTime: null,
          },
        };
      });
      await callTaskProgressApi(detailId, "pause");
    } finally {
      timerPendingRef.current = null;
      setTimerPendingId(null);
    }
  };

  const handleResumeTask = async (detailId) => {
    if (workOrderStatus === 2 && resumeWO && !skipWoPausedGuardRef.current) {
      setPendingTaskAction(detailId);
      return;
    }
    skipWoPausedGuardRef.current = false;
    if (timerPendingRef.current) return;
    timerPendingRef.current = detailId;
    setTimerPendingId(detailId);
    try {
      setTaskTimers((prev) => {
        const t = prev[detailId] || {};
        return {
          ...prev,
          [detailId]: { ...t, status: "running", lastStartTime: Date.now() },
        };
      });
      await callTaskProgressApi(detailId, "resume");
    } finally {
      timerPendingRef.current = null;
      setTimerPendingId(null);
    }
  };

  const handleCompleteTask = async (detailId) => {
    if (timerPendingRef.current) return;
    timerPendingRef.current = detailId;
    setTimerPendingId(detailId);
    try {
      setTaskTimers((prev) => {
        const t = prev[detailId] || {};
        const now = Date.now();
        const finalElapsed =
          t.status === "running"
            ? (t.baseElapsed || 0) +
              Math.floor((now - (t.lastStartTime || now)) / 60000)
            : t.elapsed || 0;
        return {
          ...prev,
          [detailId]: {
            ...t,
            status: "completed",
            elapsed: finalElapsed,
            baseElapsed: finalElapsed,
            lastStartTime: null,
          },
        };
      });
      const res = await callTaskProgressApi(detailId, "complete");
      if (res?.success && res.service_task_detail) {
        const updated = res.service_task_detail;
        setServiceTasks((prev) =>
          prev.map((t) =>
            t.service_task_detail_id === detailId
              ? {
                  ...t,
                  laborCost: +updated.labor_cost
                    ? +updated.labor_cost
                    : t.laborCost,
                  totalCost: +updated.total_cost
                    ? +updated.total_cost
                    : t.totalCost,
                }
              : t,
          ),
        );
      }
    } finally {
      timerPendingRef.current = null;
      setTimerPendingId(null);
    }
  };

  const handleOverrideTask = (detailId, initialMinutes = 0) => {
    setOverrideTask({ detailId, initialMinutes });
  };

  const handleSaveOverride = async (minutes) => {
    if (!minutes || minutes <= 0) return;
    setOverrideSaving(true);
    const res = await postApi(
      {
        querystring: "addUpdateTaskProgress",
        service_task_detail_id: overrideTask.detailId,
        task_action: "override",
        action_time: Math.floor(Date.now() / 1000),
        override_time_sec: minutes * 60,
        //timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      "workOrder",
    );
    setOverrideSaving(false);
    if (res?.success) {
      const updated = res.service_task_detail;
      if (updated) {
        setServiceTasks((prev) =>
          prev.map((t) =>
            t.service_task_detail_id === overrideTask.detailId
              ? {
                  ...t,
                  laborCost: +updated.labor_cost
                    ? +updated.labor_cost
                    : t.laborCost,
                  totalCost: +updated.total_cost
                    ? +updated.total_cost
                    : t.totalCost,
                }
              : t,
          ),
        );
        setServiceTasksChanged(true);
      }
      setTaskTimers((prev) => ({
        ...prev,
        [overrideTask.detailId]: {
          status: "completed",
          elapsed: minutes,
          baseElapsed: minutes,
          lastStartTime: null,
        },
      }));
      if (woTimerMode === 2 && updated.total_time) {
        const totalMinutes = Math.floor(updated.total_time / 60);
        setManualTime((prev) => ({
          ...prev,
          [overrideTask.detailId]: {
            hours: Math.floor(totalMinutes / 60),
            minutes: totalMinutes % 60,
          },
        }));
      }
      setOverrideTask(null);
    }
  };

  const formatElapsed = (minutes) => {
    if (!minutes) return "0m";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const formatLaborTime = (totalSeconds) => {
    if (!totalSeconds) return null;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    if (m > 0) return `${m}m`;
    return null;
  };

  const handleToggleExpand = (detailId) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(detailId)) next.delete(detailId);
      else next.add(detailId);
      return next;
    });
  };

  const handleEditPartsLabor = (task) => {
    if (tag === "essential-user") {
      setsubscribePopupErrMsg(t("subscribe_to_advanced_msg"));
      setShowSubscribePopup(true);
      return;
    }

    // Always read from Redux so we get the latest name/parts/time even after edits
    const latest =
      reduxServiceTasks.find(
        (r) => r.service_task_id === task.service_task_id,
      ) || task;
    console.log("Latest task data for editing:", latest);
    setEditPartsLaborTask({
      service_task_id: latest.service_task_id,
      service_task_name: latest.service_task_name,
      suggestedParts: Array.isArray(latest.suggestedParts)
        ? latest.suggestedParts
        : [],
      estimated_time: latest.estimated_time || 0,
      comments: latest.comments || "",
      st_category_id: latest.st_category_id || "",
      category_name: latest.category_name || "",
      recurring: latest.recurring || 0,
      veh_id: latest.veh_id || "All",
    });
    setShowEditPartsLaborPopup(true);
  };

  // Batches change callback — called by AddPartsPopupNew / UnitsConsumedPopupNew
  const handleBatchesChange = (detailId, newBatches) => {
    setServiceTasks((prev) =>
      prev.map((t) =>
        t.service_task_detail_id === detailId
          ? { ...t, batches: newBatches }
          : t,
      ),
    );
  };

  // Remove all batches for a given part_id from a task, recomputing costs
  const handleRemovePartBatches = (detailId, partId) => {
    setServiceTasks((prev) =>
      prev.map((t) => {
        if (t.service_task_detail_id !== detailId || t.action === 3) return t;
        const updatedBatches = t.batches.reduce((result, b) => {
          if (b.part_id === partId) {
            if (b.action === 2) result.push({ ...b, action: 3 });
            // action:1 batches are dropped entirely
          } else {
            result.push(b);
          }
          return result;
        }, []);
        const activeBatches = updatedBatches.filter((b) => b.action !== 3);
        const totalPartsCost = activeBatches.reduce(
          (acc, b) => acc + +b.totalCost,
          0,
        );
        const noOfParts = [...new Set(activeBatches.map((b) => b.part_id))]
          .length;
        const laborCost = t.laborCost || 0;
        return {
          ...t,
          batches: updatedBatches,
          partsCost: +totalPartsCost.toFixed(2),
          totalCost: (+totalPartsCost + +laborCost).toFixed(2),
          noOfParts,
        };
      }),
    );
    if (!serviceTasksChanged) setServiceTasksChanged(true);
  };

  // Task costs change callback — called by UnitsConsumedPopupNew on submit
  const handleTaskCostsChange = (
    detailId,
    {
      partsCost,
      //partsDisplayArr,
      noOfParts,
    },
  ) => {
    setServiceTasks((prev) =>
      prev.map((t) => {
        if (t.service_task_detail_id === detailId && t.action !== 3) {
          const laborCost = t.laborCost || 0;
          return {
            ...t,
            partsCost: +partsCost,
            totalCost: (+partsCost + +laborCost)?.toFixed(2),
            //partsDisplayArr,
            noOfParts,
          };
        }
        return t;
      }),
    );
  };

  // New State for Collapse and Templates
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [confirmationType, setConfirmationType] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);

  const { templates, templatesStatus, allServiceTaskCategories } = useSelector(
    (state) => state.servicesTasks,
  );

  useEffect(() => {
    if (templatesStatus === "idle") {
      dispatch(
        fetchServiceTaskTemplates({
          querystring: "fetch_all_service_task_templates",
        }),
      );
    }
  }, [templatesStatus, dispatch]);

  const categoryInitialized = useRef(false);
  useEffect(() => {
    if (!categoryInitialized.current) {
      categoryInitialized.current = true;
      return;
    }
    if (!selectedVehicleId) return;
    (async () => {
      const apiData = {
        querystring: "getallservicetask",
        service_veh_id: selectedVehicleId,
      };
      const refreshed = await updateServiceTaskOptions(apiData);
      setServiceTaskOptions(refreshed);
    })();
  }, [allServiceTaskCategories]);

  const hydrateTasks = (taskIds) => {
    if (!taskIds || !Array.isArray(taskIds)) return [];
    return taskIds
      .map((id) => serviceTaskOptions.find((opt) => opt.service_task_id === id))
      .filter((task) => task)
      .map((task) => ({
        ...task,
        laborCost: "",
        partsCost: "",
        totalCost: "",
        comments: "",
      }));
  };

  // Effect to sync service tasks when the selected template is edited
  useEffect(() => {
    if (!template || !template.value) return;

    const updatedTemplate = templates.find((t) => t.value === template.value);
    if (!updatedTemplate) return;

    const taskIdsChanged =
      JSON.stringify(updatedTemplate.taskIds) !==
      JSON.stringify(template.taskIds);
    const nameChanged = updatedTemplate.label !== template.label;

    if (taskIdsChanged || nameChanged) {
      setTemplate(updatedTemplate);

      if (taskIdsChanged) {
        const oldTemplateTaskIdSet = new Set(template.taskIds || []);
        const newTaskIdSet = new Set(updatedTemplate.taskIds || []);

        const activeTasks = serviceTasks.filter((t) => t.action !== 3);
        const currentTaskMap = new Map(
          activeTasks.map((t) => [t.service_task_id, t]),
        );

        const removedIds = new Set(
          activeTasks
            .filter(
              (t) =>
                oldTemplateTaskIdSet.has(t.service_task_id) &&
                !newTaskIdSet.has(t.service_task_id),
            )
            .map((t) => t.service_task_id),
        );

        const toAdd = (updatedTemplate.taskIds || [])
          .filter((id) => !currentTaskMap.has(id))
          .map((id) =>
            serviceTaskOptions.find((opt) => opt.service_task_id === id),
          )
          .filter(Boolean)
          .map((task) => ({
            ...task,
            laborCost: "",
            partsCost: "",
            totalCost: "",
            comments: "",
          }));

        setServiceTasks((prev) => {
          // Mark removed tasks as action:3 or drop action:1 ones
          const updated = prev.reduce((result, t) => {
            if (removedIds.has(t.service_task_id) && t.action !== 3) {
              if (t.action === 2) {
                const batches = (t.batches || [])
                  .filter((b) => b.action !== 1)
                  .map((b) => ({ ...b, action: 3 }));
                result.push({ ...t, action: 3, batches });
              }
              // action:1 — drop entirely
            } else {
              result.push(t);
            }
            return result;
          }, []);

          // Add new tasks from template
          const withNew = [...updated];
          toAdd.forEach((task, i) => {
            const newDetailId = `${vehicle.veh_id}_${Date.now() + withNew.length + i + 1}`;
            withNew.push({
              ...task,
              action: 1,
              service_task_detail_id: newDetailId,
              batches: [],
              comments: "",
            });
          });

          // Reorder: action:3 first (hidden), then template order, then non-template tasks
          const action3Tasks = withNew.filter((t) => t.action === 3);
          const activeMap = new Map(
            withNew
              .filter((t) => t.action !== 3)
              .map((t) => [t.service_task_id, t]),
          );
          const nonTemplateTasks = withNew.filter(
            (t) => t.action !== 3 && !newTaskIdSet.has(t.service_task_id),
          );

          return [
            ...action3Tasks,
            ...(updatedTemplate.taskIds || [])
              .map((id) => activeMap.get(id))
              .filter(Boolean),
            ...nonTemplateTasks,
          ];
        });

        if (!serviceTasksChanged) setServiceTasksChanged(true);
      }
    }
  }, [templates]);

  const isModified = useMemo(() => {
    if (!template || !template.taskIds) return false;
    const activeTasks = serviceTasks.filter((t) => t.action !== 3);
    const currentTaskIds = activeTasks.map((t) => t.service_task_id).sort();
    const templateTaskIds = [...template.taskIds].sort();
    if (currentTaskIds.length !== templateTaskIds.length) return true;
    return !currentTaskIds.every((id, index) => id === templateTaskIds[index]);
  }, [template, serviceTasks]);

  const handleTemplateChange = (selectedOption) => {
    if (selectedOption?.value === "create_new") {
      setTemplateToEdit(null);
      setShowTemplatePopup(true);
    } else if (selectedOption) {
      if (selectedOption.value === template?.value) return;
      let tasksToAdd = [];
      if (selectedOption.taskIds && selectedOption.taskIds.length > 0) {
        tasksToAdd = hydrateTasks(selectedOption.taskIds);
      }
      const activeTasks = serviceTasks.filter((t) => t.action !== 3);
      if (template || activeTasks.length > 0) {
        if (tasksToAdd.length > 0) {
          setPendingTemplate({ ...selectedOption, tasks: tasksToAdd });
          setConfirmationType("apply");
        }
      } else {
        setTemplate(selectedOption);
        if (tasksToAdd.length > 0) {
          replaceServicesWithTemplate(tasksToAdd);
        }
      }
    } else {
      if (template) {
        setConfirmationType("clear");
      }
    }
  };

  const replaceServicesWithTemplate = (tasksToAdd) => {
    setServiceTasks((prev) => {
      const activeTasks = prev.filter((t) => t.action !== 3);
      const visibleTaskIds = new Set(activeTasks.map((t) => t.service_task_id));

      // Delete all currently visible tasks
      const withDeleted = prev.reduce((result, t) => {
        if (visibleTaskIds.has(t.service_task_id) && t.action !== 3) {
          if (t.action === 2) {
            const batches = (t.batches || [])
              .filter((b) => b.action !== 1)
              .map((b) => ({ ...b, action: 3 }));
            result.push({ ...t, action: 3, batches });
          }
          // action:1 — drop
        } else {
          result.push(t);
        }
        return result;
      }, []);

      // Add new tasks
      const newTasks = tasksToAdd.map((task, i) => ({
        ...task,
        action: 1,
        service_task_detail_id: `${vehicle.veh_id}_${Date.now() + i + 1}`,
        batches: [],
        comments: "",
      }));

      return [...withDeleted, ...newTasks];
    });

    if (!serviceTasksChanged) setServiceTasksChanged(true);
  };

  const handleConfirmTemplate = () => {
    if (pendingTemplate) {
      replaceServicesWithTemplate(pendingTemplate.tasks);
      setTemplate(pendingTemplate);
      setConfirmationType(null);
      setPendingTemplate(null);
    }
  };

  const handleEditTemplate = (tmpl) => {
    setTemplateToEdit(tmpl);
    setShowTemplatePopup(true);
  };

  const handleCreateTemplate = (templateName) => {
    setNewTemplateName(templateName);
    setTemplateToEdit(null);
    setShowTemplatePopup(true);
  };

  const TemplateOption = (props) => {
    if (props.data.__isNew__) {
      return (
        <>
          <hr style={{ margin: 0, padding: 0 }} />
          <components.Option {...props}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span
                style={{ color: "#007bff", fontWeight: 300, fontSize: "12px" }}
              >
                {t("create_new_template")}
                <span style={{ fontWeight: 500 }}> "{props.data.value}"</span>
              </span>
            </div>
          </components.Option>
        </>
      );
    }

    return (
      <components.Option {...props}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{props.data.label}</span>
          <i
            className="fi fi-rr-pencil"
            style={{ cursor: "pointer", color: "#6c757d" }}
            onClick={(e) => {
              e.stopPropagation();
              handleEditTemplate(props.data);
            }}
          />
        </div>
      </components.Option>
    );
  };

  useEffect(() => {
    (async () => {
      const apiData = {
        querystring: "getallservicetask",
        service_veh_id: "All",
      };
      dispatch(fetchServicesTasks(apiData));
    })();
  }, []);

  useEffect(() => {
    const activeTasks = serviceTasks.filter((t) => t.action !== 3);
    const isUndefined = activeTasks?.some((t) => !t);
    if (activeTasks?.length > 0 && !isUndefined) {
      setShowServiceTasksError(false);
      const laborTotalArr = activeTasks
        .map((t) => t.laborCost)
        .filter((ele) => ele);
      const partsTotalArr = activeTasks
        .map((t) => t.partsCost)
        .filter((ele) => ele);
      let partsTotal = partsTotalArr.reduce((sum, n) => +sum + +n, 0);
      let laborTotal = laborTotalArr.reduce((sum, n) => +sum + +n, 0);
      if (+partsTotal) partsTotal = +partsTotal.toFixed(2);
      if (+laborTotal) laborTotal = +laborTotal.toFixed(2);
      setPartsCostTotal(partsTotal);
      setLaborCostTotal(laborTotal);
      let total = +partsTotal + +laborTotal;
      if (+total) total = +total.toFixed(2);
      setTotal(total);
      let totalCost = +partsTotal + +laborTotal - +discount + +tax;
      if (+totalCost) totalCost = totalCost.toFixed(2);
      if (totalCost >= 0) {
        setGrandTotal(totalCost);
      } else {
        setGrandTotal(0);
      }
    } else {
      setPartsCostTotal("");
      setLaborCostTotal("");
      setTotal("");
      setGrandTotal(0);
    }
  }, [serviceTasks, discount, tax]);

  const handleServiceTaskDelete = (id) => {
    setServiceTasks((prev) =>
      prev.reduce((result, t) => {
        if (t.service_task_id === id && t.action !== 3) {
          if (t.action === 2) {
            const batches = (t.batches || [])
              .filter((b) => b.action !== 1)
              .map((b) => ({ ...b, action: 3 }));
            result.push({ ...t, action: 3, batches });
          }
          // action:1 — drop entirely
        } else {
          result.push(t);
        }
        return result;
      }, []),
    );
    if (!serviceTasksChanged) setServiceTasksChanged(true);
  };

  const handlePartsChange = (e, id) => {
    const { value } = e.target;
    setServiceTasks((prev) =>
      prev.map((t) => {
        if (t.service_task_id === id && t.action !== 3) {
          return {
            ...t,
            partsCost: +value == 0 ? "" : +value,
            totalCost:
              +value == 0
                ? (+t.laborCost)?.toFixed(2)
                : (+value + +t.laborCost)?.toFixed(2),
          };
        }
        return t;
      }),
    );
    if (!serviceTasksChanged) setServiceTasksChanged(true);
  };

  const handleLaborChange = (e, id) => {
    const { value } = e.target;
    setServiceTasks((prev) =>
      prev.map((t) => {
        if (t.service_task_id === id && t.action !== 3) {
          return {
            ...t,
            laborCost: +value == 0 ? "" : +value,
            totalCost:
              +value == 0
                ? (+t.partsCost)?.toFixed(2)
                : (+value + +t.partsCost)?.toFixed(2),
          };
        }
        return t;
      }),
    );
    if (!serviceTasksChanged) setServiceTasksChanged(true);
  };

  const handleAddEditPart = (task) => {
    if (tag === "essential-user") {
      setsubscribePopupErrMsg(t("subscribe_to_advanced_msg"));
      setShowSubscribePopup(true);
      return;
    }
    setActiveDetailId(task.service_task_detail_id);
    //If the task has non action 3 batches, then show units consumed popup, else show add parts popup
    if (task.batches && task.batches.some((b) => b.action !== 3)) {
      setShowUnitsConsumedPopup(true);
    } else {
      setShowAddPartsPopup(true);
    }
  };

  const handleUseButtonClick = (task, suggestedPart) => {
    if (tag === "essential-user") {
      setsubscribePopupErrMsg(t("subscribe_to_advanced_msg"));
      setShowSubscribePopup(true);
      return;
    }
    setActiveSuggestedPart(suggestedPart);
    setActiveDetailId(task.service_task_detail_id);
    setShowAddPartsPopup(true);
  };

  const refreshServiceTaskOptions = async () => {
    const apiData = {
      querystring: "getallservicetask",
      service_veh_id: selectedVehicleId,
    };
    const serviceTasksRes = await updateServiceTaskOptions(apiData);
    setServiceTaskOptions(serviceTasksRes);
    return serviceTasksRes;
  };

  const handleServiceTaskAdded = async (newServiceTaskId) => {
    const serviceTasksRes = await refreshServiceTaskOptions();
    const newServiceTask = serviceTasksRes?.find(
      (t) => t.service_task_id === newServiceTaskId,
    );
    if (newServiceTask) {
      setServiceTasks((prev) => {
        const activeTasks = prev.filter((t) => t.action !== 3);
        if (activeTasks.every((t) => t.service_task_id != newServiceTaskId)) {
          const newDetailId = `${vehicle.veh_id}_${Date.now() + prev.length + 1}`;
          return [
            ...prev,
            {
              ...newServiceTask,
              action: 1,
              service_task_detail_id: newDetailId,
              batches: [],
              comments: "",
            },
          ];
        }
        return prev;
      });
      if (!serviceTasksChanged) setServiceTasksChanged(true);
    }
  };

  const handleServiceTaskEdited = async (savedTaskId) => {
    const serviceTasksRes = await refreshServiceTaskOptions();
    const freshTask = serviceTasksRes?.find(
      (t) => t.service_task_id === savedTaskId,
    );
    if (freshTask) {
      setServiceTasks((prev) =>
        prev.map((t) =>
          t.service_task_id === savedTaskId && t.action !== 3
            ? {
                ...t,
                suggested_parts: freshTask.suggested_parts,
                suggestedParts: freshTask.suggestedParts,
                estimated_time: freshTask.estimated_time,
              }
            : t,
        ),
      );
      if (!serviceTasksChanged) setServiceTasksChanged(true);
    }
    setEditPartsLaborTask(null);
    setShowEditPartsLaborPopup(false);
  };

  // Derive active task batches for popups
  const activeTaskBatches = useMemo(() => {
    if (!activeDetailId) return [];
    const task = serviceTasks.find(
      (t) => t.service_task_detail_id === activeDetailId && t.action !== 3,
    );
    return task?.batches || [];
  }, [activeDetailId, serviceTasks]);

  const activeTasks = serviceTasks.filter((t) => t.action !== 3);

  return (
    <>
      {overrideTask && (
        <OverrideLaborTimePopup
          showPopup={!!overrideTask}
          setShowPopup={() => setOverrideTask(null)}
          saving={overrideSaving}
          onSave={handleSaveOverride}
          initialMinutes={overrideTask.initialMinutes || 0}
        />
      )}
      {pendingTaskAction && (
        <ActionPopup
          showPopup={!!pendingTaskAction}
          setShowPopup={() => setPendingTaskAction(null)}
          message={t("start_resume_task_resume_wo_warning")}
          handleYes={async () => {
            const detailId = pendingTaskAction;
            setResumingWO(true);
            await resumeWO();
            setResumingWO(false);
            setPendingTaskAction(null);
            skipWoPausedGuardRef.current = true;
            const timerState = taskTimers[detailId];
            if (timerState?.status === "paused") {
              handleResumeTask(detailId);
            } else {
              handleStartTask(detailId);
            }
          }}
          yesBtnLoading={resumingWO}
          handleNo={() => setPendingTaskAction(null)}
        />
      )}
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={subscribePopupErrMsg}
        />
      )}
      {showEditPartsLaborPopup && (
        <ServiceTasksFormPopup
          showPopup={showEditPartsLaborPopup}
          setShowPopup={setShowEditPartsLaborPopup}
          editPartsLaborMode
          vehicleId={vehicle?.veh_id}
          editData={editPartsLaborTask}
          onServiceTaskSaved={handleServiceTaskEdited}
        />
      )}
      {showAddPartsPopup && (
        <AddPartsPopupNew
          showPopup={showAddPartsPopup}
          setShowPopup={(val) => {
            setShowAddPartsPopup(val);
            if (!val) setActiveSuggestedPart(null);
          }}
          serviceTaskDetailId={{ current: activeDetailId }}
          taskBatches={[
            ...activeTaskBatches,
            ...sessionBatches.filter((b) => b.action !== 3),
          ]}
          onNewBatchesReady={(newBatches) =>
            setSessionBatches((prev) => [...prev, ...newBatches])
          }
          showUnitsConsumedPopup={showUnitsConsumedPopup}
          setShowUnitsConsumedPopup={setShowUnitsConsumedPopup}
          setFromUnitsConsumed={setFromUnitsConsumed}
          vehicle={vehicle}
          suggestedPart={activeSuggestedPart}
        />
      )}
      {showUnitsConsumedPopup && (
        <UnitsConsumedPopupNew
          showPopup={showUnitsConsumedPopup}
          setShowPopup={setShowUnitsConsumedPopup}
          onSessionClear={() => setSessionBatches([])}
          serviceTaskDetailId={{ current: activeDetailId }}
          taskBatches={activeTaskBatches}
          sessionBatches={sessionBatches}
          onSessionUpdate={(localBatches) => {
            const taskBatchIds = new Set(
              activeTaskBatches.map((b) => b.parts_batch_id),
            );
            setSessionBatches(
              localBatches.filter((b) => !taskBatchIds.has(b.parts_batch_id)),
            );
          }}
          onBatchesChange={(newBatches) =>
            handleBatchesChange(activeDetailId, newBatches)
          }
          onTaskCostsChange={(costs) =>
            handleTaskCostsChange(activeDetailId, costs)
          }
          setShowAddPartsPopup={setShowAddPartsPopup}
          setFromUnitsConsumed={setFromUnitsConsumed}
          serviceTasksChanged={serviceTasksChanged}
          setServiceTasksChanged={setServiceTasksChanged}
        />
      )}
      {showSuggestedPartsFor &&
        (() => {
          const spTask = serviceTasks.find(
            (t) =>
              t.service_task_detail_id === showSuggestedPartsFor &&
              t.action !== 3,
          );
          if (!spTask) return null;
          const spParts = getLatestServiceTaskData(
            spTask.service_task_id,
            "suggestedParts",
            [],
          );
          const isWOInProgress =
            module === "workorder" &&
            (workOrderStatus === 1 || workOrderStatus === 2);
          return (
            <SuggestedPartsPopup
              task={spTask}
              suggestedParts={spParts}
              onHide={() => setShowSuggestedPartsFor(null)}
              onUse={(part) => {
                setShowSuggestedPartsFor(null);
                handleUseButtonClick(spTask, part);
              }}
              isWOInProgress={isWOInProgress}
              roleId={roleId}
            />
          );
        })()}
      {showLogsFor &&
        (() => {
          const logsTask = serviceTasks.find(
            (t) => t.service_task_detail_id === showLogsFor && t.action !== 3,
          );
          if (!logsTask) return null;
          return (
            <TaskLogsPopup
              task={logsTask}
              users={users}
              onHide={() => setShowLogsFor(null)}
            />
          );
        })()}
      <AddServiceModalNew
        showAddServiceModal={showAddServiceModal}
        setShowAddServiceModal={setShowAddServiceModal}
        serviceTasks={activeTasks}
        setServiceTasks={setServiceTasks}
        showAddServiceTaskFormPopup={showAddServiceTaskFormPopup}
        setShowAddServiceTaskFormPopup={setShowAddServiceTaskFormPopup}
        selectedVehicleId={selectedVehicleId}
        serviceTaskOptions={serviceTaskOptions}
        setServiceTaskOptions={setServiceTaskOptions}
        serviceTasksChanged={serviceTasksChanged}
        setServiceTasksChanged={setServiceTasksChanged}
        vehicle={vehicle}
        onServiceTaskSaved={handleServiceTaskAdded}
      />
      <div
        style={{ border: `1px solid ${colorLightGray}`, borderRadius: "5px" }}
        className={"px-2"}
      >
        <div
          className={`d-flex align-items-center justify-content-between py-2 ${
            isCollapsible ? "cursor-pointer" : ""
          }`}
          onClick={() => isCollapsible && setIsCollapsed(!isCollapsed)}
          style={isCollapsible ? { cursor: "pointer" } : {}}
        >
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2">
              <h5 style={{ fontWeight: 500, margin: 0 }}>
                {t("service_tasks")}
              </h5>
              {activeTasks.length > 0 && (
                <div
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    borderRadius: "50%",
                    width: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  {activeTasks.length}
                </div>
              )}
              {isModified && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#6c757d",
                    fontStyle: "italic",
                  }}
                >
                  ({t("modified")})
                </span>
              )}
            </div>
            {helperText && <Desc>{helperText}</Desc>}
          </div>
          {isCollapsible && (
            <i
              className={`fi fi-rr-angle-small-${isCollapsed ? "down" : "up"}`}
              style={{ fontSize: "20px", position: "relative", top: "3px" }}
            />
          )}
        </div>

        {!isCollapsed && (
          <>
            {showTemplatePopup && (
              <ServiceTaskTemplatePopupNew
                showPopup={showTemplatePopup}
                setShowPopup={setShowTemplatePopup}
                editData={templateToEdit}
                serviceTaskOptions={serviceTaskOptions}
                newTemplateName={newTemplateName}
                setServiceTaskOptions={setServiceTaskOptions}
                selectedVehicleId={selectedVehicleId}
                onClose={() => setTemplateToEdit(null)}
                onDeleteSuccess={(deletedId) => {
                  if (template?.value === deletedId) {
                    setTemplate(null);
                  }
                }}
              />
            )}
            {confirmationType && (
              <TemplateConfirmationPopup
                show={!!confirmationType}
                onHide={() => {
                  setConfirmationType(null);
                  setPendingTemplate(null);
                }}
                onConfirm={() => {
                  if (confirmationType === "apply") {
                    handleConfirmTemplate();
                  } else {
                    setTemplate(null);
                    setConfirmationType(null);
                  }
                }}
                title={
                  confirmationType === "apply"
                    ? t("apply_template_q", {
                        templateName: pendingTemplate?.label || "Template",
                      })
                    : t("clear_template")
                }
                message={
                  confirmationType === "apply"
                    ? t("apply_template_msg", {
                        templateName: pendingTemplate?.label || "Template",
                      })
                    : t("clear_template_msg")
                }
                confirmLabel={
                  confirmationType === "apply" ? t("apply") : t("ok")
                }
              />
            )}

            <Row className="pb-3">
              <Col md={12}>
                <CreatableSelect
                  options={templates}
                  onChange={handleTemplateChange}
                  onCreateOption={(value) => handleCreateTemplate(value)}
                  components={{
                    Option: TemplateOption,
                    MenuList: CustomMenuList,
                  }}
                  isClearable
                  placeholder={t("apply_create_template")}
                  styles={formStyles}
                  value={template}
                  onCreateNew={() => handleCreateTemplate("")}
                  createNewLabel={t("create_new_template")}
                />
              </Col>
            </Row>

            <div style={{ backgroundColor: colorBg }}>
              <Row className="px-3 py-2">
                <Col md={5}>
                  <FormGroupLabel className="m-0">
                    {t("task_name")}
                  </FormGroupLabel>
                </Col>
                <Col md={2}>
                  <FormGroupLabel
                    className="m-0"
                    style={{ textAlign: "right" }}
                  >{`${t("parts")} (${currency})`}</FormGroupLabel>
                </Col>
                <Col md={2}>
                  <FormGroupLabel
                    className="m-0"
                    style={{ textAlign: "right" }}
                  >{`${t("labor")} (${currency})`}</FormGroupLabel>
                </Col>
                <Col md={2}>
                  <FormGroupLabel
                    className="m-0"
                    style={{ textAlign: "right" }}
                  >{`${t("total")} (${currency})`}</FormGroupLabel>
                </Col>
              </Row>

              {activeTasks.map((task) => {
                const taskId = task?.service_task_id;
                const detailId = task?.service_task_detail_id;
                const isExpanded = expandedTaskIds.has(detailId);
                const timerState = getTimerState(detailId);
                const isWOInProgress =
                  module === "workorder" &&
                  (workOrderStatus === 1 || workOrderStatus === 2);
                const isWOClosed =
                  module === "workorder" && workOrderStatus === 3;
                // const suggestedParts = Array.isArray(task?.suggestedParts)
                //   ? task.suggestedParts
                //   : [];
                const suggestedParts = getLatestServiceTaskData(
                  taskId,
                  "suggestedParts",
                  [],
                );
                const laborTime = getLatestServiceTaskData(
                  taskId,
                  "estimated_time",
                  0,
                );
                const laborTimeLabel = formatLaborTime(laborTime);
                const noteVisible = noteInputVisible[detailId];
                return (
                  <div
                    key={detailId}
                    style={{ borderBottom: `1px solid ${colorLightGray}` }}
                  >
                    <Row className="px-3 py-2 d-flex align-items-center">
                      <Col md={5} className="p-0">
                        <p className="px-1">{task?.service_task_name}</p>
                      </Col>
                      <Col md={2}>
                        <CostInput
                          type="number"
                          onKeyDown={(e) => handleNegative(e)}
                          onWheel={handleNumberInputOnWheelPreventChange}
                          value={task?.partsCost}
                          onChange={(e) => handlePartsChange(e, taskId)}
                          disabled={roleId === 4 && isWOClosed}
                        />
                      </Col>
                      <Col md={2}>
                        <CostInput
                          type="number"
                          onWheel={handleNumberInputOnWheelPreventChange}
                          value={task?.laborCost}
                          onKeyDown={(e) => handleNegative(e)}
                          onChange={(e) => handleLaborChange(e, taskId)}
                          disabled={roleId === 4 && isWOClosed}
                        />
                      </Col>
                      <Col md={2}>
                        <CostElement>{task?.totalCost}</CostElement>
                      </Col>
                      <Col md={1}>
                        {(workOrderStatus !== 3 ||
                          (workOrderStatus === 3 && roleId === 1)) && (
                          <ActionIconContainer
                            style={{ top: 1 }}
                            onClick={() => handleServiceTaskDelete(taskId)}
                          >
                            <i className="fi fi-rr-cross-small" />
                          </ActionIconContainer>
                        )}
                      </Col>
                    </Row>

                    {/* Est. Labor Time */}
                    {/* {((module === "workorder" &&
                      !isWOInProgress &&
                      !isWOClosed) ||
                      (module === "service" && timerState.status === "idle")) &&
                      laborTimeLabel && (
                        <Row className="px-2 py-1">
                          <Col>
                            <div className="d-flex justify-content-end">
                              <Desc style={{ marginTop: "2px" }}>
                                {t("est_label")} {laborTimeLabel}
                              </Desc>
                            </div>
                          </Col>
                        </Row>
                      )} */}

                    {/* Time taken — Services only, completed tasks */}
                    {/* {module === "service" &&
                      task.initialTimerStatus === "completed" &&
                      task.initialElapsed > 0 && (
                        <Row className="px-2 pb-2">
                          <Col>
                            <TimerRow>
                              <TimerDisplay $color="#6c757d">
                                <i
                                  className="fi fi-rr-check"
                                  style={{
                                    fontSize: "10px",
                                    color: "#28a745",
                                    position: "relative",
                                    top: "1px",
                                    flexShrink: 0,
                                  }}
                                />
                                {formatElapsed(task.initialElapsed)}
                              </TimerDisplay>
                            </TimerRow>
                          </Col>
                        </Row>
                      )} */}

                    {/* Timer */}
                    {module === "workorder" &&
                      !isEquipment &&
                      woTimerMode !== 2 &&
                      (isWOInProgress ||
                        (isWOClosed && timerState.status === "completed")) && (
                        <Row className="px-2 pb-2">
                          <Col>
                            <TimerRow>
                              {timerState.status === "idle" && (
                                <div className="d-flex justify-content-between w-100 align-items-center">
                                  <TaskTimerButton
                                    type="button"
                                    $variant="start"
                                    disabled={timerPendingId === detailId}
                                    $disabled={timerPendingId === detailId}
                                    onClick={() => handleStartTask(detailId)}
                                  >
                                    <i
                                      className="fi fi-sr-play"
                                      style={{
                                        fontSize: "8px",
                                        marginRight: "5px",
                                        position: "relative",
                                        //top: "1px",
                                      }}
                                    />
                                    {t("start_task")}
                                  </TaskTimerButton>
                                  {laborTimeLabel && (
                                    <Desc className="my-2">
                                      {t("est_label")} {laborTimeLabel}
                                    </Desc>
                                  )}
                                </div>
                              )}
                              {timerState.status === "running" && (
                                <div className="d-flex justify-content-between w-100">
                                  <div className="d-flex gap-2">
                                    <TaskTimerButton
                                      type="button"
                                      $variant="pause"
                                      disabled={timerPendingId === detailId}
                                      $disabled={timerPendingId === detailId}
                                      onClick={() => handlePauseTask(detailId)}
                                    >
                                      <i
                                        className="fi fi-sr-pause"
                                        style={{
                                          fontSize: "8px",
                                          marginRight: "5px",
                                          position: "relative",
                                        }}
                                      />
                                      {t("pause_task")}
                                    </TaskTimerButton>
                                    <TaskTimerButton
                                      type="button"
                                      $variant="complete"
                                      disabled={timerPendingId === detailId}
                                      $disabled={timerPendingId === detailId}
                                      onClick={() =>
                                        handleCompleteTask(detailId)
                                      }
                                    >
                                      <i
                                        className="fi fi-rr-check"
                                        style={{
                                          fontSize: "8px",
                                          marginRight: "5px",
                                          position: "relative",
                                        }}
                                      />
                                      {t("complete_task")}
                                    </TaskTimerButton>
                                  </div>
                                  <div className="d-flex flex-column align-items-end">
                                    <TimerDisplay $color="#28a745">
                                      <BlinkingDot />
                                      {formatElapsed(timerState.elapsed)}
                                    </TimerDisplay>
                                  </div>
                                </div>
                              )}
                              {timerState.status === "paused" && (
                                <div className="d-flex justify-content-between w-100">
                                  <div className="d-flex gap-2">
                                    <TaskTimerButton
                                      type="button"
                                      $variant="resume"
                                      disabled={timerPendingId === detailId}
                                      $disabled={timerPendingId === detailId}
                                      onClick={() => handleResumeTask(detailId)}
                                    >
                                      <i
                                        className="fi fi-sr-play"
                                        style={{
                                          fontSize: "8px",
                                          marginRight: "5px",
                                          position: "relative",
                                        }}
                                      />
                                      {t("resume_task")}
                                    </TaskTimerButton>
                                    <TaskTimerButton
                                      type="button"
                                      $variant="complete"
                                      disabled={timerPendingId === detailId}
                                      $disabled={timerPendingId === detailId}
                                      onClick={() =>
                                        handleCompleteTask(detailId)
                                      }
                                    >
                                      <i
                                        className="fi fi-rr-check"
                                        style={{
                                          fontSize: "8px",
                                          marginRight: "5px",
                                          position: "relative",
                                        }}
                                      />
                                      {t("complete_task")}
                                    </TaskTimerButton>
                                  </div>
                                  <div className="d-flex flex-column align-items-end">
                                    <TimerDisplay $color="#fd7e14">
                                      <i
                                        className="fi fi-sr-pause"
                                        style={{
                                          fontSize: "9px",
                                          position: "relative",
                                          top: "1px",
                                          flexShrink: 0,
                                        }}
                                      />
                                      {formatElapsed(timerState.elapsed)}
                                    </TimerDisplay>
                                  </div>
                                </div>
                              )}
                              {timerState.status === "completed" && (
                                <div className="d-flex justify-content-between w-100">
                                  <div className="d-flex gap-2 align-items-center">
                                    <TaskTimerButton
                                      type="button"
                                      $variant="completed"
                                      $disabled
                                      disabled
                                    >
                                      <i
                                        className="fi fi-rr-check"
                                        style={{
                                          fontSize: "9px",
                                          marginRight: "5px",
                                          position: "relative",
                                          top: "1px",
                                        }}
                                      />
                                      {t("task_completed_label")}
                                    </TaskTimerButton>
                                    {(roleId === 1 || roleId === 2) && (
                                      <OverrirdeLink
                                        $variant="override"
                                        onClick={() =>
                                          handleOverrideTask(
                                            detailId,
                                            timerState.elapsed,
                                          )
                                        }
                                      >
                                        {t("override")}
                                      </OverrirdeLink>
                                    )}
                                  </div>
                                  <div className="d-flex flex-column align-items-end">
                                    <TimerDisplay $color={"#6c757d"}>
                                      <i
                                        className="fi fi-rr-check"
                                        style={{
                                          fontSize: "10px",
                                          color: "#28a745",
                                          position: "relative",
                                          top: "1px",
                                          flexShrink: 0,
                                        }}
                                      />
                                      {formatElapsed(timerState.elapsed)}
                                    </TimerDisplay>
                                    {/* {laborTimeLabel && (
                                    <Desc>
                                      {t("est_label")} {laborTimeLabel}
                                    </Desc>
                                  )} */}
                                  </div>
                                </div>
                              )}
                            </TimerRow>
                          </Col>
                        </Row>
                      )}

                    {/* Manual Entry Timer */}
                    {module === "workorder" &&
                      !isEquipment &&
                      woTimerMode === 2 && (
                        <Row className="px-2 pb-2">
                          <Col>
                            <TimerRow>
                              {isWOInProgress && (
                                <div className="d-flex justify-content-between w-100 align-items-center">
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      style={{ fontSize: 12, color: "#555" }}
                                    >
                                      {t("time_spent")}:
                                    </span>
                                    <TimeSelect
                                      value={manualTime[detailId]?.hours ?? 0}
                                      onChange={(e) =>
                                        handleManualTimeChange(
                                          detailId,
                                          "hours",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      {Array.from({ length: 51 }, (_, i) => (
                                        <option key={i} value={i}>
                                          {i}h
                                        </option>
                                      ))}
                                    </TimeSelect>
                                    <TimeSelect
                                      value={manualTime[detailId]?.minutes ?? 0}
                                      onChange={(e) =>
                                        handleManualTimeChange(
                                          detailId,
                                          "minutes",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      {[0, 15, 30, 45].map((m) => (
                                        <option key={m} value={m}>
                                          {m}m
                                        </option>
                                      ))}
                                    </TimeSelect>
                                  </div>
                                  {/* {laborTimeLabel && (
                                  <Desc className="my-2">
                                    {t("est_label")} {laborTimeLabel}
                                  </Desc>
                                )} */}
                                </div>
                              )}
                              {isWOClosed &&
                                (() => {
                                  const manualEntry = manualTime[detailId];
                                  const closedElapsed = manualEntry
                                    ? manualEntry.hours * 60 +
                                      manualEntry.minutes
                                    : task.initialElapsed;
                                  if (!closedElapsed) return null;
                                  return (
                                    <div className="d-flex justify-content-between w-100 align-items-center">
                                      <div className="d-flex gap-2 align-items-center">
                                        <TimerDisplay $color="#6c757d">
                                          <i
                                            className="fi fi-rr-check"
                                            style={{
                                              fontSize: 10,
                                              color: "#28a745",
                                              position: "relative",
                                              top: "1px",
                                              flexShrink: 0,
                                            }}
                                          />
                                          {formatElapsed(closedElapsed)}
                                        </TimerDisplay>
                                        {(roleId === 1 || roleId === 2) && (
                                          <OverrirdeLink
                                            $variant="override"
                                            onClick={() =>
                                              handleOverrideTask(
                                                detailId,
                                                closedElapsed,
                                              )
                                            }
                                          >
                                            {t("override")}
                                          </OverrirdeLink>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                            </TimerRow>
                          </Col>
                        </Row>
                      )}

                    <Row className="px-2 pb-2">
                      <Col>
                        <PartsNotesToggle
                          onClick={() => handleToggleExpand(detailId)}
                        >
                          <i
                            className={`fi fi-rr-angle-small-${isExpanded ? "down" : "right"}`}
                            style={{ position: "relative", top: "2px" }}
                          />
                          {isEquipment ? t("notes") : t("parts_and_notes")}
                        </PartsNotesToggle>
                      </Col>
                    </Row>

                    {isExpanded && (
                      <TaskExpandedSection>
                        {/* Suggested parts + Est. labor + Edit — all on one line (workorder only) */}
                        {module === "workorder" &&
                          (() => {
                            const usedCount = suggestedParts.filter((p) =>
                              task.batches?.some(
                                (b) =>
                                  b.part_id === p.part_id && b.action !== 3,
                              ),
                            ).length;
                            const canEdit = !isWOInProgress && !isWOClosed;
                            const suggestedPartsLabel =
                              usedCount > 0
                                ? t("suggested_parts_used", {
                                    used: usedCount,
                                    total: suggestedParts.length,
                                  })
                                : `${t("suggested_parts")} (${suggestedParts.length})`;
                            return (
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  {suggestedParts.length > 0 ? (
                                    <LinkSpan
                                      onClick={() => {
                                        if (tag === "essential-user") {
                                          setsubscribePopupErrMsg(
                                            t("subscribe_to_advanced_msg"),
                                          );
                                          setShowSubscribePopup(true);
                                          return;
                                        }
                                        setShowSuggestedPartsFor(detailId);
                                      }}
                                    >
                                      {suggestedPartsLabel}
                                    </LinkSpan>
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#888",
                                      }}
                                    >
                                      {`${t("suggested_parts")} (0)`}
                                    </span>
                                  )}
                                  <span
                                    style={{ fontSize: "13px", color: "#888" }}
                                  >
                                    ·
                                  </span>
                                  <Desc style={{ margin: 0 }}>
                                    {t("est_label")} {laborTimeLabel || "—"}
                                  </Desc>
                                </div>
                                {canEdit && (
                                  <EditIcon
                                    className="fi fi-rr-pencil"
                                    onClick={() => handleEditPartsLabor(task)}
                                  ></EditIcon>
                                )}
                              </div>
                            );
                          })()}

                        {/* Parts used from inventory (read-only, derived from batches) */}
                        {(() => {
                          const activeBatches = (task.batches || []).filter(
                            (b) => b.action !== 3,
                          );
                          // group by part_id
                          const grouped = activeBatches.reduce((acc, b) => {
                            if (!acc[b.part_id]) {
                              acc[b.part_id] = {
                                part_id: b.part_id,
                                part_name: b.part_name,
                                part_number: b.part_number,
                                qty: 0,
                              };
                            }
                            acc[b.part_id].qty += +(b.unitsConsumed || 0);
                            return acc;
                          }, {});
                          const partsUsed = Object.values(grouped);
                          if (partsUsed.length === 0) return null;
                          return (
                            <div className="mb-1 mt-3">
                              <PartsUsedLabel>{t("parts_used")}</PartsUsedLabel>
                              {partsUsed.map((p) => (
                                <PartsUsedRow key={p.part_id}>
                                  <div className="d-flex align-items-center gap-1">
                                    <i
                                      className="fi fi-rr-check"
                                      style={{
                                        fontSize: "10px",
                                        color: "#28a745",
                                        position: "relative",
                                        top: "1px",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <p style={{ fontSize: "13px" }}>
                                      {p.part_name}
                                      {p.part_number && (
                                        <span style={{ color: "#888" }}>
                                          {" "}
                                          ({p.part_number})
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      style={{
                                        fontSize: "13px",
                                        color: "#555",
                                      }}
                                    >
                                      {t("qty")}: {p.qty}
                                    </span>
                                    {(workOrderStatus !== 3 ||
                                      (workOrderStatus === 3 &&
                                        [1, 2].includes(roleId))) && (
                                      <ActionIconContainer
                                        style={{ top: 0 }}
                                        onClick={() =>
                                          handleRemovePartBatches(
                                            detailId,
                                            p.part_id,
                                          )
                                        }
                                      >
                                        <i className="fi fi-rr-cross-small" />
                                      </ActionIconContainer>
                                    )}
                                  </div>
                                </PartsUsedRow>
                              ))}
                            </div>
                          );
                        })()}

                        {/* +Select Part link */}
                        {(module === "service" ||
                          workOrderStatus !== 3 ||
                          (workOrderStatus === 3 && [1, 2].includes(roleId))) &&
                          !isEquipment && (
                            <div className="mb-2">
                              <LinkSpan onClick={() => handleAddEditPart(task)}>
                                {t("select_part_add")}
                              </LinkSpan>
                            </div>
                          )}

                        <div className="mb-2">
                          {!noteVisible ? (
                            task.comments ? (
                              <NotePreviewContainer
                                onClick={() =>
                                  setNoteInputVisible((prev) => ({
                                    ...prev,
                                    [detailId]: true,
                                  }))
                                }
                              >
                                <NoteIcon className="fi fi-rr-memo-pad" />
                                <NotePreview>{task.comments}</NotePreview>
                              </NotePreviewContainer>
                            ) : (
                              <OverrirdeLink
                                onClick={() =>
                                  setNoteInputVisible((prev) => ({
                                    ...prev,
                                    [detailId]: true,
                                  }))
                                }
                              >
                                {t("add_note")}
                              </OverrirdeLink>
                            )
                          ) : (
                            <NoteTextareaWithClickAway
                              value={task.comments || ""}
                              placeholder={t("add_note")}
                              onChange={(e) =>
                                setServiceTasks((prev) =>
                                  prev.map((t) =>
                                    t.service_task_detail_id === detailId
                                      ? { ...t, comments: e.target.value }
                                      : t,
                                  ),
                                )
                              }
                              onClose={() =>
                                setNoteInputVisible((prev) => ({
                                  ...prev,
                                  [detailId]: false,
                                }))
                              }
                            />
                          )}
                        </div>

                        {/* Task logs link */}
                        {module === "workorder" && task.logs?.length > 0 && (
                          <div className="mb-2">
                            <LinkSpan onClick={() => setShowLogsFor(detailId)}>
                              {`${t("task_log")} (${task.logs.length})`}
                            </LinkSpan>
                          </div>
                        )}
                      </TaskExpandedSection>
                    )}
                  </div>
                );
              })}
              {(workOrderStatus !== 3 ||
                (workOrderStatus === 3 && [1, 2].includes(roleId))) && (
                <>
                  <Row className="px-4 pt-2 pb-0">
                    <AddButton
                      handleClick={() => setShowAddServiceModal(true)}
                      label={t("add_task")}
                    />
                  </Row>
                  <Row className="pt-2 pb-0 m-0">
                    <hr className="p-0 m-0" />
                  </Row>
                </>
              )}
              <Row className="px-4 py-2">
                <Col md={5}>
                  <p>{t("total")}</p>
                </Col>
                <Col md={2}>
                  <CostElement>{partsCostTotal}</CostElement>
                </Col>
                <Col md={2}>
                  <CostElement>{laborCostTotal}</CostElement>
                </Col>
                <Col md={2}>
                  <CostElement style={{ padding: "0 0.25rem" }}>
                    {total}
                  </CostElement>
                </Col>
              </Row>
            </div>
            {showServiceTasksError && <Error error={serviceTasksErrorMsg} />}

            <Row className="py-3">
              <Col className="d-flex justify-content-end align-items-end flex-column gap-3">
                <div className="d-flex align-items-center justify-content-center">
                  <FormGroupLabel htmlFor="discount" className="m-0 px-2">
                    {t("discount")}
                  </FormGroupLabel>
                  <FormItem style={{ width: "140px" }}>
                    <input
                      name="discount"
                      id="discount-input"
                      type="number"
                      onWheel={handleNumberInputOnWheelPreventChange}
                      onChange={(e) => setDiscount(e.target.value)}
                      value={discount}
                      onKeyDown={(e) => handleNegative(e)}
                      style={{ ...formInputStyles }}
                    />
                    <UnitSpan>{currency}</UnitSpan>
                  </FormItem>
                </div>
                {showDiscountError && (
                  <Error error={t("discount_greater_than_total_err_msg")} />
                )}

                <div className="d-flex align-items-center justify-content-center">
                  <FormGroupLabel htmlFor="tax" className="m-0 px-2">
                    {t("tax")}
                  </FormGroupLabel>
                  <FormItem style={{ width: "140px" }}>
                    <input
                      name="tax"
                      id="tax-input"
                      type="number"
                      onWheel={handleNumberInputOnWheelPreventChange}
                      value={tax}
                      onChange={(e) => setTax(e.target.value)}
                      onKeyDown={(e) => handleNegative(e)}
                      style={{ ...formInputStyles }}
                    />
                    <UnitSpan>{currency}</UnitSpan>
                  </FormItem>
                </div>

                <div className="d-flex align-items-center justify-content-center">
                  <FormGroupLabel className="m-0 px-2">
                    {t("grand_total")}
                  </FormGroupLabel>
                  <span style={{ fontWeight: 500, width: "140px" }}>
                    {grandTotal ? `${grandTotal} ${currency}` : NATEXT}
                  </span>
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default ServiceTasksNew;

// AddServiceModal variant for ServiceTasksNew — uses setServiceTasks instead of serviceTasksRef
export const AddServiceModalNew = ({
  showAddServiceModal,
  setShowAddServiceModal,
  serviceTasks, // already filtered to action !== 3
  setServiceTasks,
  selectedVehicleId,
  serviceTaskOptions,
  setServiceTaskOptions,
  serviceTasksChanged,
  setServiceTasksChanged,
  vehicle,
  onServiceTaskSaved,
}) => {
  const { t } = useTranslation("common");
  const [selectedTasks, setSelectedTasks] = useState([]);
  const newTaskInfo = useRef("");
  const [showAddServiceTaskFormPopup, setShowAddServiceTaskFormPopup] =
    useState(false);

  const [serviceTasksToShow, setServiceTasksToShow] =
    useState(serviceTaskOptions);

  useEffect(() => {
    if (serviceTasks.length > 0) {
      const serviceTaskIds = serviceTasks.map((t) => t.service_task_id);
      setServiceTasksToShow(
        serviceTaskOptions.filter(
          (task) => !serviceTaskIds.includes(task.service_task_id),
        ),
      );
    } else {
      setServiceTasksToShow(serviceTaskOptions);
    }
  }, [serviceTaskOptions, serviceTasks]);

  const groupedServiceTasksToShow = useMemo(() => {
    const groups = {};
    const uncategorized = [];
    serviceTasksToShow.forEach((task) => {
      const catId = task.st_category_id;
      const catName = task.category_name;
      if (!catId && !catName) {
        uncategorized.push(task);
      } else {
        if (!groups[catId]) {
          groups[catId] = { label: catName, options: [] };
        }
        groups[catId].options.push(task);
      }
    });
    const result = Object.values(groups);
    if (uncategorized.length > 0) {
      result.push({ label: t("uncategorized"), options: uncategorized });
    }
    return result;
  }, [serviceTasksToShow, t]);

  useEffect(() => {
    setSelectedTasks([]);
  }, [showAddServiceModal]);

  const handleOK = () => {
    if (selectedTasks.length > 0) {
      setServiceTasks((prev) => {
        const newTasks = selectedTasks.map((task, i) => ({
          ...task,
          action: 1,
          service_task_detail_id: `${vehicle.veh_id}_${Date.now() + prev.length + i + 1}`,
          batches: [],
          comments: "",
        }));
        return [...prev, ...newTasks];
      });
      if (!serviceTasksChanged) setServiceTasksChanged(true);
    }
    setShowAddServiceModal(false);
  };

  const handleCreate = (value) => {
    newTaskInfo.current = { taskName: value, vehicleId: selectedVehicleId };
    setShowAddServiceModal(false);
    setShowAddServiceTaskFormPopup(true);
  };

  return (
    <>
      {showAddServiceTaskFormPopup && (
        <ServiceTasksFormPopup
          showPopup={showAddServiceTaskFormPopup}
          setShowPopup={setShowAddServiceTaskFormPopup}
          //newTaskInfo={newTaskInfo}
          newTaskName={newTaskInfo.current.taskName}
          vehicleId={newTaskInfo.current.vehicleId}
          onServiceTaskSaved={onServiceTaskSaved}
          assetIsEquipment={vehicle?.asset_type === 1}
        />
      )}
      <Modal
        show={showAddServiceModal}
        onHide={() => setShowAddServiceModal((prev) => !prev)}
        size="lg"
      >
        <Modal.Body>
          <div className="d-flex justify-content-end mb-2">
            <ActionIconContainer
              onClick={() => setShowAddServiceModal((prev) => !prev)}
            >
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
          <div
            style={{
              height: "calc(var(--window-height, 100vh) - 170px)",
            }}
          >
            <CreatableSelect
              options={groupedServiceTasksToShow}
              value={selectedTasks}
              onChange={setSelectedTasks}
              onCreateOption={handleCreate}
              placeholder={t("service_task_placeholder")}
              styles={{
                ...openDropdownStyles,
                menu: (provided) => ({
                  ...provided,
                  margin: 0,
                  height: "calc(var(--window-height, 100vh) - 240px)",
                  border: `1px solid ${colorLightGray}`,
                  boxShadow: "none",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: "0 6px",
                  margin: 0,
                  maxHeight: "56px",
                  overflowY: "auto",
                }),
              }}
              defaultMenuIsOpen
              isMulti
              menuIsOpen
              isClearable
              isSearchable
              closeMenuOnSelect={false}
              controlShouldRenderValue={true}
              hideSelectedOptions={false}
              components={{
                Option: MultiValueOption,
                MenuList: CustomMenuList,
                GroupHeading: StickyGroupHeading,
              }}
              onCreateNew={() => handleCreate("")}
              createNewLabel={t("create_new_service_task")}
              customProps={{
                type: "serviceTask",
              }}
            />
          </div>
          <div className="d-flex justify-content-end align-items-center">
            <AddFormButton onClick={handleOK}>{t("ok")}</AddFormButton>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const PartsUsedLabel = styled.p`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  //color: #888;
  //margin: 0 0 4px 0;
`;

const PartsUsedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  // border-bottom: 1px solid ${colorLightGray};
  // &:last-child {
  //   border-bottom: none;
  // }
`;

const PartsNotesToggle = styled.span`
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  user-select: none;
  &:hover {
    color: ${colorTextNew};
  }
`;

const TaskExpandedSection = styled.div`
  background: ${colorBg};
  margin: 0 12px 4px 12px;
  border-radius: 4px;
  padding: 0 8px;
`;

const SuggestedPartsList = styled.div`
  display: flex;
  flex-direction: column;
  //margin-bottom: 8px;
`;

const SuggestedPartRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px;
  border-bottom: 1px solid ${colorLightGray};
  &:last-child {
    border-bottom: none;
  }
`;

const SuggestedPartName = styled.p`
  font-size: 13px;
  font-weight: 500;
  margin: 0;
`;

const SuggestedPartNumber = styled.p`
  font-size: 11px;
  color: #888;
  margin: 0;
`;

const UseButton = styled.button`
  border: 1px solid ${colorLightGray};
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 12px;
  cursor: pointer;
  color: #333;
  flex-shrink: 0;
  &:hover {
    background: ${colorLightGray};
  }
`;

const AddedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #28a745;
  flex-shrink: 0;
`;

const NotePreviewContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
  width: 100%;
  min-width: 0;

  &:hover ${() => NotePreview}, &:hover ${() => NoteIcon} {
    color: ${colorGray};
  }
`;

const NotePreview = styled.p`
  font-size: 13px;
  color: #333;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
  flex: 1;
`;

const NoteIcon = styled.i`
  font-size: 13px;
  color: #333;
  position: relative;
  top: 1px;
`;

const TaskLogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colorLightGray};
  border-radius: 5px;
  background: white;
  padding: 0 12px;
`;

const TaskLogRow = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid ${colorLightGray};
  &:last-child {
    border-bottom: none;
  }
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 64px;
  font-size: 13px;
  border: 1px solid ${colorLightGray};
  border-radius: 4px;
  padding: 6px 8px;
  resize: vertical;
  outline: none;
  background: #fff !important;
  &:focus {
    border-color: #007bff;
  }
`;

const TimerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TimerDisplay = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.$color || "#333"};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const blinkAnimation = `
  @keyframes timerDotBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.2; }
  }
`;

const BlinkingDot = styled.span`
  ${blinkAnimation}
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #28a745;
  flex-shrink: 0;
  animation: timerDotBlink 1.2s ease-in-out infinite;
`;

const TaskTimerButton = styled.button`
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  height: min-content;
  //cursor: pointer;
  background: ${(p) => {
    if (p.$variant === "start") return "#f0fdf4";
    if (p.$variant === "pause") return "#fffbeb";
    if (p.$variant === "resume") return "#eff6ff";
    if (p.$variant === "complete") return "#1e3a5f";
    if (p.$variant === "completed") return "#f0fdf4";
    return "#007bff";
  }};
  color: ${(p) => {
    if (p.$variant === "start") return "#16a34a";
    if (p.$variant === "pause") return "#d97706";
    if (p.$variant === "resume") return "#2563eb";
    if (p.$variant === "complete") return "#fff";
    if (p.$variant === "completed") return "#16a34a";
    return "#007bff";
  }};
  border: 1px solid ${colorLightGray};
  ${(p) => p.$variant === "completed" && `opacity: 0.6;`}
  ${(p) =>
    !p.$disabled &&
    `
    cursor: pointer;
    &:hover {opacity: 0.80;}
    `}
`;

const OverrirdeLink = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colorGray};
  cursor: pointer;
  width: max-content;

  text-decoration: none;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  text-decoration-skip-ink: auto;

  &:hover,
  &:focus-visible {
    text-decoration-line: underline;
    color: ${colorAdditional};
  }
`;

const TimeSelect = styled.select`
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 12px;
  background: white;
  cursor: pointer;
`;

export const OverrideLaborTimePopup = ({
  showPopup,
  setShowPopup,
  onSave,
  saving,
  initialMinutes = 0,
}) => {
  const { t } = useTranslation("common");
  const snapMinutes = (m) => (Math.round(m / 15) * 15) % 60;
  const snapHours = (totalMin) => {
    const snapped = Math.round(totalMin / 15) * 15;
    return Math.floor(snapped / 60);
  };

  const [hours, setHours] = useState(snapHours(initialMinutes));
  const [minutes, setMinutes] = useState(snapMinutes(initialMinutes % 60));

  useEffect(() => {
    if (showPopup) {
      setHours(snapHours(initialMinutes));
      setMinutes(snapMinutes(initialMinutes % 60));
    }
  }, [showPopup]);

  const totalMinutes = hours * 60 + minutes;
  const isDirty = totalMinutes !== initialMinutes;

  return (
    <Modal show={showPopup} centered size="sm" style={{ zIndex: 1060 }}>
      <Modal.Header>
        <h5 style={{ margin: 0, fontWeight: 500 }}>
          {t("override_labor_time")}
        </h5>
        <ActionIconContainer onClick={() => setShowPopup(false)}>
          <i className="fi fi-rr-cross-small" />
        </ActionIconContainer>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: 12, color: "#555" }}>
            {t("time_spent")}:
          </span>
          <TimeSelect
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
          >
            {Array.from({ length: 51 }, (_, i) => (
              <option key={i} value={i}>
                {i}h
              </option>
            ))}
          </TimeSelect>
          <TimeSelect
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
          >
            {[0, 15, 30, 45].map((m) => (
              <option key={m} value={m}>
                {m}m
              </option>
            ))}
          </TimeSelect>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <AddFormButton
          type="button"
          onClick={() => onSave(totalMinutes)}
          disabled={totalMinutes <= 0 || !isDirty || saving}
        >
          {saving ? t("saving") : t("save")}
        </AddFormButton>
      </Modal.Footer>
    </Modal>
  );
};

const NoteTextareaWithClickAway = ({
  value,
  placeholder,
  onChange,
  onClose,
}) => {
  const clickAwayRef = useClickAway({ handleCloseElement: onClose });
  return (
    <NoteTextarea
      ref={clickAwayRef}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
    />
  );
};

const SuggestedPartsPopup = ({
  task,
  suggestedParts,
  onHide,
  onUse,
  isWOInProgress,
  roleId,
}) => {
  const { t } = useTranslation("common");
  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header>
        <h4>{`${t("suggested_parts")} -  ${task.service_task_name}`}</h4>
        <ActionIconContainer onClick={onHide}>
          <i className="fi fi-rr-cross-small" />
        </ActionIconContainer>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <SuggestedPartsList>
          {suggestedParts.map((p) => (
            <SuggestedPartRow key={p.part_id}>
              <div>
                <SuggestedPartName>{p.part_name}</SuggestedPartName>
                {p.part_number && (
                  <SuggestedPartNumber>{p.part_number}</SuggestedPartNumber>
                )}
              </div>
              {task.batches?.some(
                (b) => b.part_id === p.part_id && b.action !== 3,
              ) ? (
                <AddedBadge>
                  <i
                    className="fi fi-rr-check"
                    style={{
                      position: "relative",
                      top: "2px",
                      marginRight: "4px",
                    }}
                  />
                  {t("added")}
                </AddedBadge>
              ) : (
                (isWOInProgress || [1, 2].includes(roleId)) && (
                  <UseButton type="button" onClick={() => onUse(p)}>
                    {t("use")}
                  </UseButton>
                )
              )}
            </SuggestedPartRow>
          ))}
        </SuggestedPartsList>
      </Modal.Body>
    </Modal>
  );
};

// taskLogStatusMap is defined in shared/constants and imported above; re-exported for backward compatibility
export { taskLogStatusMap };

export const TaskLogsPopup = ({ task, users, onHide }) => {
  const { t } = useTranslation("common");
  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header>
        <h4>{`${t("task_log")} \u2014 ${task.service_task_name}`}</h4>
        <ActionIconContainer onClick={onHide}>
          <i className="fi fi-rr-cross-small" />
        </ActionIconContainer>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <TaskLogsContainer style={{ border: "none", padding: 0 }}>
          {[...task.logs].reverse().map((log, idx) => {
            const logUser = users?.find((u) => u.user_id === log.user_id);
            const userName = logUser?.name || t("unassigned_user");
            const logDate = new Date(log.time * 1000);
            const timeStr = logDate.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
            const dateStr = logDate.toLocaleDateString([], {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
            return (
              <TaskLogRow key={idx}>
                <p style={{ margin: 0, fontSize: "13px", color: "#333" }}>
                  {t(taskLogStatusMap[log.status])} <strong>{userName}</strong>
                </p>
                <p style={{ margin: 0, fontSize: "12px", color: "#888" }}>
                  {dateStr}, {timeStr}
                </p>
              </TaskLogRow>
            );
          })}
        </TaskLogsContainer>
      </Modal.Body>
    </Modal>
  );
};
